import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import appointmentService from '../../../../services/appointment.service';
import surveyService from '../../../../services/survey.service';
import { showError, showSuccess } from '../../../../utils/ecxSwal';
import Pagination from '../../../common/Pagination';
import SurveyFollowupsSkeleton from './SurveyFollowupsSkeleton';
import {
    SURVEY_CHANNELS,
    buildDefaultChannelSelection,
    buildSurveySendJobs,
    formatAppointmentDateTime,
    formatPatientName,
    getChannelState,
    getRowChannelSelection,
    getRowKey,
    getSendableRowKeys,
    isRowSendable,
    resolvePreferredChannel,
} from './surveyFollowupChannel';
import {
    buildDefaultSurveySelections,
    getRowSurveyOptions,
    getRowSurveySelection,
    getSurveyTitle,
} from './surveyFollowupSurvey';
import {
    buildSurveyFollowupsPrintSubtitle,
    printSurveyFollowupsList,
} from './surveyFollowupsPrint';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

function SurveyFollowupsPanel({ refreshToken = 0, onCountUpdate }) {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [records, setRecords] = useState([]);
    const [activeSurveys, setActiveSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [surveysLoading, setSurveysLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [channelSelections, setChannelSelections] = useState({});
    const [surveySelections, setSurveySelections] = useState({});
    const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());
    const [sending, setSending] = useState(false);
    const selectAllRef = useRef(null);

    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const fetchActiveSurveys = useCallback(async () => {
        if (!clinicId) {
            return;
        }

        setSurveysLoading(true);

        try {
            const res = await surveyService.getClinicSurveys(clinicId, 0, 100, {
                sortBy: 'title',
                sortOrder: 'ASC',
                statusFilter: 'ACTIVE',
            });
            setActiveSurveys(res.data.content || []);
        } catch (err) {
            console.error('Failed to load active surveys', err);
            toast.error('Failed to load active surveys');
            setActiveSurveys([]);
        } finally {
            setSurveysLoading(false);
        }
    }, [clinicId]);

    const fetchRecords = useCallback(
        async (page, size, options = {}) => {
            const { silent = false } = options;

            if (!clinicId) {
                return;
            }

            if (!silent) {
                setLoading(true);
            }

            try {
                const res = await appointmentService.getSurveyFollowups(clinicId, page, size);
                const data = res.data;
                const content = data.content || [];

                setRecords(content);
                setPageNumber(data.number ?? page);
                setPageSize(data.size ?? size);
                setTotalPages(data.totalPages ?? 0);
                setTotalElements(data.totalElements ?? 0);
                setChannelSelections(
                    content.reduce((acc, record) => {
                        acc[getRowKey(record)] = buildDefaultChannelSelection(record);
                        return acc;
                    }, {})
                );
                onCountUpdate?.(data.totalElements ?? 0);
            } catch (err) {
                console.error('Failed to load survey followups', err);
                toast.error('Failed to load survey followups');
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [clinicId, onCountUpdate]
    );

    useEffect(() => {
        fetchActiveSurveys();
    }, [fetchActiveSurveys]);

    useEffect(() => {
        fetchRecords(pageNumber, pageSize);
    }, [fetchRecords, pageNumber, pageSize]);

    useEffect(() => {
        if (loading || surveysLoading) {
            return;
        }

        setSurveySelections(buildDefaultSurveySelections(records, activeSurveys));
        setSelectedRowKeys(new Set(getSendableRowKeys(records, activeSurveys)));
    }, [records, activeSurveys, loading, surveysLoading]);

    useEffect(() => {
        if (!refreshToken) {
            return;
        }

        const refresh = async () => {
            setIsRefreshing(true);
            try {
                await Promise.all([
                    fetchActiveSurveys(),
                    fetchRecords(pageNumber, pageSize, { silent: true }),
                ]);
            } finally {
                setIsRefreshing(false);
            }
        };

        refresh();
    }, [refreshToken, fetchActiveSurveys, fetchRecords, pageNumber, pageSize]);

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setPageNumber(0);
    };

    const handleChannelSelect = (rowKey, channelKey, disabled) => {
        if (disabled) {
            return;
        }

        setChannelSelections((prev) => ({
            ...prev,
            [rowKey]: channelKey,
        }));
    };

    const handleSurveySelect = (rowKey, surveyId) => {
        setSurveySelections((prev) => ({
            ...prev,
            [rowKey]: surveyId,
        }));
    };

    const sendableRowKeys = useMemo(
        () => getSendableRowKeys(records, activeSurveys),
        [records, activeSurveys]
    );

    const allSendableSelected =
        sendableRowKeys.length > 0 && sendableRowKeys.every((rowKey) => selectedRowKeys.has(rowKey));

    const someSendableSelected = sendableRowKeys.some((rowKey) => selectedRowKeys.has(rowKey));

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someSendableSelected && !allSendableSelected;
        }
    }, [someSendableSelected, allSendableSelected]);

    const handleToggleSelectAll = () => {
        if (allSendableSelected) {
            setSelectedRowKeys(new Set());
            return;
        }

        setSelectedRowKeys(new Set(sendableRowKeys));
    };

    const handleToggleRow = (rowKey, record) => {
        if (!isRowSendable(record, activeSurveys)) {
            return;
        }

        setSelectedRowKeys((prev) => {
            const next = new Set(prev);

            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }

            return next;
        });
    };

    const selectedSendJobs = useMemo(
        () => buildSurveySendJobs(records, selectedRowKeys, channelSelections, surveySelections, activeSurveys),
        [records, selectedRowKeys, channelSelections, surveySelections, activeSurveys]
    );

    const handleSendSurvey = async () => {
        if (!clinicId) {
            return;
        }

        const jobs = selectedSendJobs;

        if (!jobs.length) {
            toast.info('Select at least one patient with an available survey and email channel.');
            return;
        }

        const result = await Swal.fire({
            title: 'Send survey emails?',
            html: `Queue <strong>${jobs.length} survey email${jobs.length === 1 ? '' : 's'}</strong> for the selected patients.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Send',
            cancelButtonText: 'Cancel',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'btn btn-primary rounded-pill px-3 me-2',
                cancelButton: 'btn btn-outline-secondary rounded-pill px-3 ecx-swal__btn ecx-swal__btn--cancel',
            },
        });

        if (!result.isConfirmed) {
            return;
        }

        setSending(true);

        try {
            const response = await appointmentService.sendSurveyFollowups(
                clinicId,
                jobs.map((job) => ({
                    patientId: job.patientId,
                    appointmentId: job.appointmentId,
                    surveyId: job.surveyId,
                    mediumType: job.mediumType,
                }))
            );

            const { queued = 0, failed = 0 } = response.data || {};

            if (queued > 0) {
                showSuccess({
                    title: 'Survey Queued',
                    text:
                        failed > 0
                            ? `${queued} message${queued === 1 ? '' : 's'} queued. ${failed} failed.`
                            : `${queued} survey message${queued === 1 ? '' : 's'} queued for delivery.`,
                });
                await fetchRecords(pageNumber, pageSize, { silent: true });
            } else {
                showError({
                    title: 'Send Failed',
                    text: 'Could not queue survey messages for the selected patients.',
                });
            }
        } catch (error) {
            console.error('Failed to queue survey reminders', error);
            showError({
                title: 'Send Failed',
                text: 'Could not queue survey messages for the selected patients.',
            });
        } finally {
            setSending(false);
        }
    };

    const toolbarMeta = useMemo(
        () => ({
            pageLabel: `Page ${totalPages === 0 ? 0 : pageNumber + 1} of ${Math.max(totalPages, 1)}`,
            totalLabel: `${totalElements} patient${totalElements === 1 ? '' : 's'}`,
        }),
        [pageNumber, totalPages, totalElements]
    );

    const handlePrint = () => {
        const printed = printSurveyFollowupsList({
            records,
            channelSelections,
            surveySelections,
            activeSurveys,
            title: 'Patient Engagement — Surveys',
            subtitle: buildSurveyFollowupsPrintSubtitle({
                recordsCount: records.length,
                totalElements,
                pageNumber,
                totalPages,
            }),
        });

        if (!printed) {
            toast.error('Unable to open print preview. Please try again.');
        }
    };

    const tableLoading = loading || surveysLoading;
    const tableBusy = tableLoading || sending;

    return (
        <div className="d-flex flex-column h-100">
            <div className="ecx-page-panel__header">
                <div className="thank-you-surveys-toolbar">
                    <div className="thank-you-surveys-toolbar__meta">
                        <h5 className="mb-0 fw-normal text-dark">Surveys</h5>
                        <span className="ecx-page-chip">
                            <i className="bi bi-list-ul"></i>
                            {toolbarMeta.totalLabel}
                        </span>
                        <span className="ecx-page-chip">
                            <i className="bi bi-layers"></i>
                            {toolbarMeta.pageLabel}
                        </span>
                    </div>

                    <div className="thank-you-surveys-toolbar__controls">
                        <button
                            type="button"
                            className="btn btn-sm btn-primary rounded-pill"
                            onClick={handleSendSurvey}
                            disabled={tableBusy || selectedSendJobs.length === 0}
                            aria-busy={sending}
                        >
                            {sending ? (
                                <>
                                    <span
                                        className="spinner-border spinner-border-sm me-1"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-send me-1" />
                                    Send Survey
                                    {selectedSendJobs.length > 0 ? ` (${selectedSendJobs.length})` : ''}
                                </>
                            )}
                        </button>

                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handlePrint}
                            disabled={tableBusy || records.length === 0}
                        >
                            <i className="bi bi-printer me-1"></i>
                            Print
                        </button>

                        <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                            Show
                            <select
                                className="form-select form-select-sm"
                                style={{ width: '5rem' }}
                                value={pageSize}
                                onChange={handlePageSizeChange}
                                disabled={tableBusy}
                            >
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                            entries
                        </label>
                    </div>
                </div>
            </div>

            <div className="ecx-page-panel__body d-flex flex-column p-0 position-relative">
                {sending && (
                    <div className="thank-you-surveys-sending-overlay" role="status" aria-live="polite">
                        <div className="thank-you-surveys-sending-overlay__content">
                            <span
                                className="spinner-border text-primary thank-you-surveys-sending-overlay__spinner"
                                role="status"
                                aria-hidden="true"
                            />
                            <p className="thank-you-surveys-sending-overlay__title mb-1">Sending survey messages</p>
                            <p className="thank-you-surveys-sending-overlay__subtitle mb-0">
                                Queueing {selectedSendJobs.length} message
                                {selectedSendJobs.length === 1 ? '' : 's'}...
                            </p>
                        </div>
                    </div>
                )}
                {tableLoading ? (
                    <SurveyFollowupsSkeleton count={pageSize > 8 ? 8 : pageSize} />
                ) : records.length > 0 ? (
                    <>
                        <div className="table-responsive thank-you-surveys-table-wrap">
                            <table className="table table-hover align-middle mb-0 thank-you-surveys-table">
                                <thead>
                                    <tr>
                                        <th className="thank-you-surveys-select-col ps-3">
                                            <input
                                                ref={selectAllRef}
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={allSendableSelected}
                                                disabled={sendableRowKeys.length === 0 || tableBusy}
                                                onChange={handleToggleSelectAll}
                                                aria-label="Select all patients on this page"
                                            />
                                        </th>
                                        <th>Patient</th>
                                        <th>Survey</th>
                                        <th>Email</th>
                                        <th>Cell Phone</th>
                                        <th>Appointment</th>
                                        {SURVEY_CHANNELS.map((channel) => (
                                            <th key={channel.key} className="text-center">
                                                {channel.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.map((record) => {
                                        const rowKey = getRowKey(record);
                                        const patientName = formatPatientName(record);
                                        const preferredChannel = resolvePreferredChannel(record);
                                        const selection = getRowChannelSelection(channelSelections, rowKey, record);
                                        const surveyOptions = getRowSurveyOptions(record, activeSurveys);
                                        const selectedSurveyId = getRowSurveySelection(
                                            surveySelections,
                                            rowKey,
                                            record,
                                            activeSurveys
                                        );
                                        const rowSendable = isRowSendable(record, activeSurveys);
                                        const rowSelected = selectedRowKeys.has(rowKey);

                                        return (
                                            <tr
                                                key={rowKey}
                                                className={`${rowSelected ? 'thank-you-surveys-row--selected' : ''}${
                                                    !rowSendable ? ' thank-you-surveys-row--disabled' : ''
                                                }`}
                                            >
                                                <td className="thank-you-surveys-select-col ps-3">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={rowSelected}
                                                        disabled={!rowSendable || tableBusy}
                                                        onChange={() => handleToggleRow(rowKey, record)}
                                                        aria-label={`Select ${patientName}`}
                                                    />
                                                </td>
                                                <td className="thank-you-surveys-patient">
                                                    <Link
                                                        to={`/patient-files/${record.id}`}
                                                        className="thank-you-surveys-patient-link fw-normal"
                                                    >
                                                        {patientName}
                                                    </Link>
                                                </td>
                                                <td className="thank-you-surveys-survey">
                                                    {surveyOptions.length > 0 ? (
                                                        <select
                                                            className="form-select form-select-sm thank-you-surveys-survey-select"
                                                            value={selectedSurveyId || ''}
                                                            disabled={!rowSendable || tableBusy}
                                                            onChange={(event) =>
                                                                handleSurveySelect(rowKey, event.target.value)
                                                            }
                                                            aria-label={`Survey for ${patientName}`}
                                                        >
                                                            {surveyOptions.map((survey) => (
                                                                <option key={survey.id} value={survey.id}>
                                                                    {survey.title || 'Untitled survey'}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <span
                                                            className="text-muted small"
                                                            title="No active clinic or doctor survey is available for this appointment"
                                                        >
                                                            No survey available
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="thank-you-surveys-contact">
                                                    {record.email || <span className="text-muted">—</span>}
                                                </td>
                                                <td className="thank-you-surveys-contact">
                                                    {record.cellPhone || <span className="text-muted">—</span>}
                                                </td>
                                                <td className="thank-you-surveys-appointment">
                                                    {formatAppointmentDateTime(record)}
                                                </td>
                                                {SURVEY_CHANNELS.map((channel) => {
                                                    const state = getChannelState(record, channel.key);
                                                    const isPreferred = preferredChannel === channel.key;
                                                    const checked = selection === channel.key;
                                                    const title = state.disabled
                                                        ? !rowSendable && surveyOptions.length === 0
                                                            ? 'No active survey available for this appointment'
                                                            : record.isSurvey === false
                                                              ? 'Survey reminders disabled in communication preferences'
                                                              : !state.available
                                                                ? 'No email contact on file'
                                                                : 'Email disabled in communication preferences'
                                                        : isPreferred
                                                          ? 'Preferred email channel'
                                                          : channel.label;

                                                    return (
                                                        <td key={`${rowKey}-${channel.key}`} className="text-center">
                                                            <div
                                                                className={`thank-you-surveys-channel ${
                                                                    isPreferred ? 'is-preferred' : ''
                                                                } ${state.disabled || !rowSendable ? 'is-disabled' : ''}`}
                                                                title={title}
                                                            >
                                                                <input
                                                                    type="radio"
                                                                    name={`survey-channel-${rowKey}`}
                                                                    className="form-check-input"
                                                                    checked={checked}
                                                                    disabled={state.disabled || !rowSendable || tableBusy}
                                                                    onChange={() =>
                                                                        handleChannelSelect(rowKey, channel.key, state.disabled)
                                                                    }
                                                                    aria-label={`${channel.label} for ${patientName}`}
                                                                />
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="ecx-page-pagination border-top p-3 mt-auto">
                                <Pagination
                                    currentPage={pageNumber}
                                    totalPages={totalPages}
                                    totalElements={totalElements}
                                    pageSize={pageSize}
                                    onPageChange={setPageNumber}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="ecx-page-empty flex-grow-1 thank-you-surveys-panel--surveys-empty">
                        <div className="ecx-page-empty__icon">
                            <i className="bi bi-clipboard-check"></i>
                        </div>
                        <h5 className="text-dark fw-normal mb-2">No survey followups</h5>
                        <p className="mb-0">
                            There are no confirmed appointments from the last 7 days.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SurveyFollowupsPanel;
